import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { BehaviorConstellationState } from "@/lib/livingConstellation";
import { formatDistanceToNow } from "date-fns";

function formatContextTag(tag: string): string {
  const normalized = tag.replace(/[_-]+/g, " ").trim();
  if (!normalized) return tag;

  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildNoticingCopy(node: BehaviorConstellationState): string {
  if (node.state === "strengthening") {
    return `${node.behaviorName} is showing up more consistently across recent meetings.`;
  }
  if (node.state === "mixed") {
    return `${node.behaviorName} appears in some moments, with counter-pressure in others.`;
  }
  if (node.state === "receding") {
    return `${node.behaviorName} is present, but opposite dynamics are showing up more often right now.`;
  }
  if (node.state === "emerging") {
    return `${node.behaviorName} is beginning to emerge in recent meeting evidence.`;
  }
  return `${node.behaviorName} has limited recent evidence so far.`;
}

function buildReflectionPrompt(node: BehaviorConstellationState): string {
  if (node.state === "receding") {
    return "What helps you hold this behavior when meetings become more pressured?";
  }
  if (node.state === "mixed") {
    return "Which meeting contexts make this behavior easier for you to sustain?";
  }
  if (node.state === "strengthening") {
    return "What would help you carry this pattern into your next high-stakes meeting?";
  }
  if (node.state === "emerging") {
    return "Where could you intentionally practice this behavior again this week?";
  }
  return "What small moment this week could become evidence for this behavior?";
}

function buildPatternSummary(node: BehaviorConstellationState): string {
  const support = node.supportingMeetingCount30d;
  const opposition = node.opposingMeetingCount30d;

  if (node.state === "receding") {
    return `${support} supporting and ${opposition} opposing meeting signals in the last 30 days.`;
  }
  if (node.state === "mixed") {
    return `${support} supporting and ${opposition} opposing meeting signals in the last 30 days.`;
  }
  return `${support} supporting meeting signals in the last 30 days.`;
}

function getSourceLabel(sourceType: BehaviorConstellationState["recentEvidence"][number]["sourceType"]): string {
  if (sourceType === "behavioral_intelligence") return "Behavioral signal";
  if (sourceType === "context_graph") return "Context graph";
  return "Transcript evidence";
}

interface LivingConstellationDetailPanelProps {
  node: BehaviorConstellationState;
}

export function LivingConstellationDetailPanel({ node }: LivingConstellationDetailPanelProps) {
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.07] via-background to-background">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{node.behaviorName}</CardTitle>
          <Badge variant="outline" className="text-xs uppercase">{node.theme}</Badge>
          <Badge variant="secondary" className="text-xs capitalize">{node.state}</Badge>
          {node.phase && (
            <Badge variant="outline" className="text-[10px] uppercase text-muted-foreground">
              Path: {node.phase.toLowerCase().split("_").join(" ")}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <section className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What Veera is interpreting</h4>
          <p className="text-sm text-foreground/90">{buildNoticingCopy(node)}</p>
        </section>

        <section className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Where this tends to show up</h4>
          {node.contextTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {node.contextTags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-[11px]">
                  {formatContextTag(tag)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Not enough contextual spread yet.</p>
          )}
        </section>

        <section className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent pattern</h4>
          <p className="text-sm text-foreground/90">{buildPatternSummary(node)}</p>
        </section>

        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent evidence</h4>
          {node.recentEvidence.length > 0 ? (
            <div className="space-y-2">
              {node.recentEvidence.map((item) => (
                <div key={`${item.meetingId}-${item.polarity}-${item.sourceType}`} className="rounded-lg border bg-background/70 p-3">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{item.meetingTitle}</p>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        {getSourceLabel(item.sourceType)}
                      </Badge>
                      <Badge variant={item.polarity === "supporting" ? "secondary" : "outline"} className="text-[10px] capitalize">
                        {item.polarity}
                      </Badge>
                    </div>
                  </div>
                  {item.excerpt && (
                    <p className="text-xs text-foreground/85">{item.excerpt}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {item.confidence} confidence
                    </Badge>
                    {item.contextTags.slice(0, 2).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-[10px]">
                        {formatContextTag(tag)}
                    </Badge>
                  ))}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {formatDistanceToNow(new Date(item.meetingStartTime), { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No meeting excerpts are available yet.</p>
          )}
        </section>

        <section className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reflection</h4>
          <p className="text-sm italic text-foreground/90">{buildReflectionPrompt(node)}</p>
        </section>
      </CardContent>
    </Card>
  );
}
